<template>
    <teleport to="body" v-if="showContainer">
        <div class="fixed z-40 inset-0 w-full h-full p-4 pb-0 flex flex-col justify-end md:pb-4 md:items-center md:justify-center">
            <transition
                enter-from-class="opacity-0"
                enter-active-class="transition ease-in-out duration-300"
                enter-to-class="opacity-100"
                leave-from-class="opacity-100"
                leave-active-class="transition ease-in-out duration-100 delay-200"
                leave-to-class="opacity-0"
                @before-enter="backgroundEntering = true"
                @after-enter="backgroundEntering = false"
                @before-leave="backgroundLeaving = true"
                @after-leave="backgroundLeaving = false"
                :appear="appear"
            >
                <div v-if="showContent" @click.self="close()" class="absolute inset-0 w-full h-full bg-gray-700 bg-opacity-30"></div>
            </transition>
            <transition
                enter-from-class="translate-y-full md:translate-y-0 md:scale-110 md:opacity-0"
                enter-active-class="transition ease-in-out duration-200 delay-100"
                enter-to-class="translate-y-0 md:scale-100 md:opacity-100"
                leave-from-class="translate-y-0 md:scale-100 md:opacity-100"
                leave-active-class="transition ease-in-out duration-300"
                leave-to-class="translate-y-full md:translate-y-0 md:scale-110 md:opacity-0"
                @before-enter="foregroundEntering = true"
                @after-enter="foregroundEntering = false"
                @before-leave="foregroundLeaving = true"
                @after-leave="foregroundLeaving = false"
                :appear="appear"
            >
                <div
                    v-if="showContent"
                    class="relative p-4 w-full max-h-full bg-white rounded-t flex flex-col gap-4 overflow-hidden md:w-auto md:max-w-screen-sm md:rounded-b shadow-lg"
                >
                    <slot name="default" />

                    <slot
                        name="footer"
                        :close="close"
                    >
                        <button
                            @click.prevent="close()"
                            class="btn btn-green block w-full"
                        >
                            Close
                        </button>
                    </slot>
                </div>
            </transition>
        </div>
    </teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

const emit = defineEmits(['update:modelValue', 'closed'])

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    closeable: {
        type: Boolean,
        default: true,
    },
    appear: {
        type: Boolean,
        default: false,
    }
})

const showContainer = ref(props.modelValue)
const showContent = ref(props.modelValue)

const backgroundLeaving = ref(false)
const foregroundLeaving = ref(false)
const backgroundEntering = ref(false)
const foregroundEntering = ref(false)

const show = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    }
})
const leaving = computed(() => {
    return backgroundLeaving.value || foregroundLeaving.value
})

const entering = computed(() => {
    return backgroundEntering.value || foregroundEntering.value
})

const close = (force = false) => {
    if(entering.value) return
    if(!props.closeable && !force) return
    show.value = false
}

const onEscape = (event) => {
    if(!show.value) return
    if(!['Esc', 'Escape'].includes(event.key)) return
    close()
}

watch(leaving, (value) => {
    if(value) return
    showContainer.value = value
})

watch(showContainer, (value) => {
    if(!value) return emit('closed')
    nextTick(() => {
        showContent.value = value
    })
})

watch(
    show,
    (value) => {
        if(value) {
            showContainer.value = value
            document.body.classList.add('overflow-hidden')
            window.addEventListener('keyup', onEscape)
        } else {
            showContent.value = value
            document.body.classList.remove('overflow-hidden')
            window.removeEventListener('keyup', onEscape)
        }
    },
    { immediate: true }
)
</script>
